import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import { NotebookCellResource, NotebookTypes } from '@mode/notebooks/contract';
import * as CellActions from './notebook-cell.actions';
import * as NotebookActions from '../notebook/notebook.actions';
export const notebookCellFeatureKey = 'notebookcell';
export function removeKey<T>(state: Record<string, T>, keyToRemove: string): Record<string, T> {
  const { [keyToRemove]: _, ...rest } = state;
  return rest;
}

export interface NotebookCellFeatureState {
  selectedNotebookToken: string | null;
  selectedCellToken: string | null;
  isResultSelected: boolean;
  isEditing: boolean;
  tokens: string[];
  contents: Record<string, string>;
  frozen: Record<string, boolean>;
  folded: Record<string, boolean>;
  contentTypes: Record<string, NotebookTypes.ContentType>;
  createdAt: Record<string, Date>;
  resources: Record<string, NotebookCellResource>;
  executing: Record<string, boolean>;
  lastRunTokens: Record<string, string>;
  missingRuns: Record<string, boolean>;
  inLayout: Record<string, boolean>;
}

export const notebookCellInitialState: NotebookCellFeatureState = {
  selectedNotebookToken: null,
  selectedCellToken: null,
  isResultSelected: false,
  isEditing: false,
  tokens: [],
  contents: {},
  frozen: {},
  folded: {},
  contentTypes: {},
  createdAt: {},
  resources: {},
  executing: {},
  lastRunTokens: {},
  missingRuns: {},
  inLayout: {},
};

export const selectedNotebookTokenReducer = createReducer(notebookCellInitialState.selectedNotebookToken);

export const selectedCellTokenReducer = createReducer(
  notebookCellInitialState.selectedCellToken,
  on(CellActions.addCellSuccess, (_, { cellResource }) => cellResource.token),
  on(CellActions.beginEditCell, (_, { cellToken }) => cellToken),
  on(CellActions.selectCell, (_, { cellToken }) => cellToken),
  on(CellActions.destroyCell, (_, { cellToken, nextCellToken, notebookToken, notebookCellTokensArray }) => {
    if (nextCellToken) {
      return nextCellToken;
    }
    const tokens = notebookCellTokensArray.get(notebookToken) ?? [];
    if (tokens.length > 1) {
      const index = tokens.indexOf(cellToken);
      if (index > 0) {
        return tokens[index - 1];
      }
    }

    return null;
  })
);

export const isResultSelectedReducer = createReducer(
  notebookCellInitialState.isResultSelected,
  on(CellActions.beginEditCell, (_, { cellToken }) => false),
  on(CellActions.selectCell, (resultPane) => resultPane)
);

export const isEditingReducer = createReducer(
  notebookCellInitialState.isEditing,
  on(CellActions.addCellSuccess, () => true),
  on(CellActions.updateCell, CellActions.selectCell, () => false),
  on(CellActions.beginEditCell, (state, { cellFrozen }) => !cellFrozen),
  on(CellActions.destroyCell, () => false)
);

export const cellTokensReducer = createReducer(
  notebookCellInitialState.tokens,
  on(CellActions.addCellSuccess, (state, { cellResource, nextToken }) => {
    const newTokens = [...state];
    if (nextToken) {
      const idx = newTokens.indexOf(nextToken);
      if (idx >= 0) newTokens.splice(idx, 0, cellResource.token);
      else newTokens.push(cellResource.token);
    } else {
      newTokens.push(cellResource.token);
    }
    return newTokens;
  }),
  on(CellActions.destroyCell, (state, { cellToken }) => state.filter((token) => token !== cellToken)),
  on(CellActions.destroyCellFailure, (state, { cellToken }) =>
    state.includes(cellToken) ? state : [...state, cellToken]
  )
);

export const contentsReducer = createReducer(
  notebookCellInitialState.contents,
  on(CellActions.addCellSuccess, (state, { cellResource }) => ({
    ...state,
    [cellResource.token]: cellResource.content,
  })),
  on(CellActions.editCellContent, (state, { content, cellToken }) => ({
    ...state,
    [cellToken]: content,
  })),
  on(CellActions.updateCellSuccess, (state, { cellResource }) => ({
    ...state,
    [cellResource.token]: cellResource.content,
  })),
  on(CellActions.updateCellFailure, (state, { cellToken, original, error }) => ({
    ...state,
    [cellToken]: original.content,
  })),
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken))
);

export const frozenReducer = createReducer(
  notebookCellInitialState.frozen,
  on(CellActions.addCellSuccess, (state, { cellResource }) => ({
    ...state,
    [cellResource.token]: cellResource.frozen_in_layout,
  })),
  on(CellActions.updateContentType, (state, { contentType, cellToken }) => ({
    ...state,
    [cellToken]: false,
  })),
  on(CellActions.updateContentTypeFailure, (state, { cellToken, original, error }) => ({
    ...state,
    [cellToken]: original.frozen_in_layout,
  })),
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken)),
  // Handle successful freeze state update from API
  on(CellActions.updateCellSuccess, (state, { cellResource }) => ({
    ...state,
    [cellResource.token]: cellResource.frozen_in_layout,
  })),
  // Handle toggleFreezeCell by optimistically toggling frozen state
  on(CellActions.toggleFreezeCell, (state, { cellToken }) => ({
    ...state,
    [cellToken]: !state[cellToken],
  })),
  // Handle toggleFreezeCellFailure by reverting frozen state
  on(CellActions.toggleFreezeCellFailure, (state, { cellToken }) => ({
    ...state,
    [cellToken]: !state[cellToken], // Revert the optimistic update
  }))
);

export const foldedReducer = createReducer(
  notebookCellInitialState.folded,
  on(CellActions.selectCell, (state, { cellToken }) => ({
    ...state,
    [cellToken]: false,
  })),
  // Handle foldCell by optimistically toggling folded state
  on(CellActions.foldCell, (state, { cellToken }) => ({
    ...state,
    [cellToken]: !state[cellToken],
  })),
  // Handle fold cell success by toggling folded state
  on(CellActions.foldCellSuccess, (state, { cellToken }) => ({
    ...state,
    [cellToken]: !state[cellToken],
  }))
);

export const contentTypesReducer = createReducer(
  notebookCellInitialState.contentTypes,
  on(CellActions.addCellSuccess, (state, { cellResource }) => ({
    ...state,
    [cellResource.token]: cellResource.content_type,
  })),
  on(CellActions.updateContentType, (state, { contentType, cellToken }) => ({
    ...state,
    [cellToken]: contentType,
  })),
  on(CellActions.updateContentTypeFailure, (state, { cellToken, original, error }) => ({
    ...state,
    [cellToken]: original.content_type,
  })),
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken))
);

export const createdAtReducer = createReducer(
  notebookCellInitialState.createdAt,
  on(CellActions.addCellSuccess, (state, { cellResource }) => ({
    ...state,
    [cellResource.token]: cellResource.created_at,
  })),
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken))
);

export const resourcesReducer = createReducer(
  notebookCellInitialState.resources,
  on(CellActions.addCellSuccess, (state, { cellResource }) => ({ ...state, [cellResource.token]: cellResource })),
  on(CellActions.updateCellSuccess, (state, { cellResource }) => ({ ...state, [cellResource.token]: cellResource })),
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken))
);

export const executingReducer = createReducer(
  notebookCellInitialState.executing,
  on(CellActions.addCellSuccess, (state, { cellResource }) => ({ ...state, [cellResource.token]: false })),
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken)),
  on(NotebookActions.initSession, NotebookActions.cancelRun, (state) => {
    const anyExecuting = Object.values(state).some(Boolean);
    if (!anyExecuting) return state;

    const updated: Record<string, boolean> = {};
    for (const token in state) {
      updated[token] = false;
    }
    return updated;
  })
);

export const lastRunTokensReducer = createReducer(
  notebookCellInitialState.lastRunTokens,
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken))
);

export const missingRunsReducer = createReducer(
  notebookCellInitialState.missingRuns,
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => {
    const { [cellToken]: _, ...rest } = state;
    return rest;
  }),
  on(
    // NotebookActions.initSession,
    // NotebookActions.runFailed,
    // NotebookActions.cancelRun,
    // NotebookActions.disconnected, Up coming actions
    NotebookActions.terminated,
    (state) => {
      // Set all values to false if at least one is true
      const shouldReset = Object.values(state).some(Boolean);
      if (!shouldReset) return state;

      const resetState: Record<string, boolean> = {};
      for (const token of Object.keys(state)) {
        resetState[token] = false;
      }
      return resetState;
    }
  )
);

export const inLayoutReducer = createReducer(
  notebookCellInitialState.inLayout,
  on(CellActions.addCellToLayoutSuccess, (state, { cellToken }) => ({
    ...state,
    [cellToken]: true,
  })),
  on(CellActions.addCellToLayoutFailure, (state, { cellToken }) => ({
    ...state,
    [cellToken]: false,
  })),
  on(CellActions.removeCellFromLayoutSuccess, (state, { cellToken }) => ({
    ...state,
    [cellToken]: false,
  })),
  on(CellActions.removeCellFromLayoutFailure, (state, { cellToken }) => ({
    ...state,
    [cellToken]: true,
  })),
  on(CellActions.destroyCellSuccess, (state, { cellToken }) => removeKey(state, cellToken))
);
export const notebookCellReducer: ActionReducerMap<NotebookCellFeatureState> = {
  selectedNotebookToken: selectedNotebookTokenReducer,
  selectedCellToken: selectedCellTokenReducer,
  isResultSelected: isResultSelectedReducer,
  isEditing: isEditingReducer,
  tokens: cellTokensReducer,
  contents: contentsReducer,
  frozen: frozenReducer,
  folded: foldedReducer,
  contentTypes: contentTypesReducer,
  createdAt: createdAtReducer,
  resources: resourcesReducer,
  executing: executingReducer,
  lastRunTokens: lastRunTokensReducer,
  missingRuns: missingRunsReducer,
  inLayout: inLayoutReducer,
};
